<template>
    <v-dialog v-model="isActive" max-width="700" @click:outside="closeHandle">
        <div class="bulk-operation-results">
            <div class="bulk-operation-results__head head">
                <h3 class="head__title">
                    {{ $t('pages.shipments.bulkOperationResults') }}
                </h3>
                <v-btn icon color="#55595c" @click="closeHandle">
                    <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
            </div>

            <div class="bulk-operation-results__body body">
                <div class="body__summary summary">
                    <div class="summary__message">
                        <v-icon
                            :color="hasFailures ? 'warning' : 'success'"
                            class="summary__icon"
                        >
                            {{
                                hasFailures
                                    ? 'mdi-alert-circle-outline'
                                    : 'mdi-check-circle-outline'
                            }}
                        </v-icon>
                        <p class="summary__text">
                            {{
                                hasFailures
                                    ? $t(
                                          'pages.shipments.bulkOperationPartialSuccess',
                                          {
                                              processed:
                                                  resultsData.processed_items,
                                              total: resultsData.total_items,
                                          }
                                      )
                                    : $t('pages.shipments.bulkOperationAllSuccess')
                            }}
                        </p>
                    </div>
                </div>

                <div v-if="hasFailures" class="body__section">
                    <h4 class="body__section-title body__section-title--error">
                        <v-icon color="error" small>mdi-close-circle</v-icon>
                        {{ $t('pages.shipments.failedItems') }}
                        ({{ failedItems.length }})
                    </h4>
                    <ul class="body__items-list items-list">
                        <li
                            v-for="(item, index) in failedItems"
                            :key="`failed-${index}`"
                            class="items-list__item items-list__item--error"
                        >
                            <div class="items-list__item-info">
                                <div class="items-list__item-id">
                                    {{ $t('pages.shipments.shipmentItemId') }}:
                                    {{ item.shipment_item_id }}
                                </div>
                                <div
                                    v-if="item.message"
                                    class="items-list__item-message"
                                >
                                    {{ formatErrorMessage(item.message) }}
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>

                <div v-if="hasSuccesses" class="body__section">
                    <h4 class="body__section-title body__section-title--success">
                        <v-icon color="success" small>mdi-check-circle</v-icon>
                        {{ $t('pages.shipments.successfulItems') }}
                        ({{ successfulItems.length }})
                    </h4>
                    <ul class="body__items-list items-list">
                        <li
                            v-for="(item, index) in successfulItems"
                            :key="`success-${index}`"
                            class="items-list__item items-list__item--success"
                        >
                            <div class="items-list__item-info">
                                <div class="items-list__item-id">
                                    {{ $t('pages.shipments.shipmentItemId') }}:
                                    {{ item.shipment_item_id }}
                                </div>
                                <div
                                    v-if="item.message"
                                    class="items-list__item-message items-list__item-message--success"
                                >
                                    {{ item.message }}
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
            </div>

            <div class="bulk-operation-results__footer footer">
                <v-btn color="#3bafda" dark small @click="closeHandle">
                    {{ textContent['close'] }}
                </v-btn>
            </div>
        </div>
    </v-dialog>
</template>

<script>
import modalsMixin from '@/mixins/modals.mixin'
import languageMixin from '@/mixins/language.mixin'
import popUpTypes from '@/types/pop-up-types'

const { shipmentBulkOperationResults } = popUpTypes

export default {
    name: 'ShipmentBulkOperationResultsModal',
    mixins: [modalsMixin, languageMixin],
    data() {
        return {
            selfName: shipmentBulkOperationResults,
        }
    },
    computed: {
        resultsData() {
            return this.extraData?.resultsData || {}
        },
        results() {
            return this.resultsData?.results || []
        },
        failedItems() {
            return this.results.filter((item) => item.status === 'error')
        },
        successfulItems() {
            return this.results.filter((item) => item.status === 'success')
        },
        hasFailures() {
            return this.failedItems.length > 0
        },
        hasSuccesses() {
            return this.successfulItems.length > 0
        },
    },
    methods: {
        formatErrorMessage(message) {
            if (!message) return ''
            const lines = message.split('\n')
            const firstLine = lines[0] || message
            try {
                const jsonMatch = firstLine.match(/\{.*"client_message":"([^"]+)".*\}/)
                if (jsonMatch && jsonMatch[1]) {
                    return jsonMatch[1]
                }
            } catch (e) {
                console.error(e)
            }
            return firstLine.length > 200
                ? firstLine.substring(0, 200) + '...'
                : firstLine
        },
    },
}
</script>

<style lang="scss" scoped>
.bulk-operation-results {
    background: $color-primary-light;

    .head {
        padding: 15px 10px 5px;
        border-bottom: 1px solid #eceeef;
        @include main-head;
        display: flex;
        justify-content: space-between;
        align-items: center;

        &__title {
            margin: 0;
            font-size: 18px;
            font-weight: 600;
        }
    }

    .body {
        padding: 16px;
        max-height: 60vh;
        overflow-y: auto;

        &__summary {
            margin-bottom: 20px;
        }

        &__section {
            margin-bottom: 24px;

            &:last-child {
                margin-bottom: 0;
            }
        }

        &__section-title {
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 16px;
            font-weight: 600;
            margin-bottom: 12px;
            padding-bottom: 8px;
            border-bottom: 2px solid #eceeef;

            &--error {
                color: #d32f2f;
                border-bottom-color: #ffcdd2;
            }

            &--success {
                color: #388e3c;
                border-bottom-color: #c8e6c9;
            }
        }

        &__items-list {
            list-style: none;
            padding: 0;
            margin: 0;
        }
    }

    .summary {
        &__message {
            display: flex;
            align-items: flex-start;
            gap: 12px;
            padding: 12px;
            background: #f5f5f5;
            border-radius: 4px;
        }

        &__icon {
            flex-shrink: 0;
            margin-top: 2px;
        }

        &__text {
            margin: 0;
            font-size: 14px;
            line-height: 1.5;
        }
    }

    .items-list {
        &__item {
            padding: 12px;
            margin-bottom: 8px;
            border-radius: 4px;
            border-left: 4px solid;

            &--error {
                background: #ffebee;
                border-left-color: #d32f2f;
            }

            &--success {
                background: #e8f5e9;
                border-left-color: #388e3c;
            }

            &:last-child {
                margin-bottom: 0;
            }
        }

        &__item-info {
            display: flex;
            flex-direction: column;
            gap: 6px;
        }

        &__item-id {
            font-weight: 600;
            font-size: 14px;
        }

        &__item-message {
            font-size: 13px;
            color: #666;
            line-height: 1.4;
            word-break: break-word;

            &--success {
                color: #2e7d32;
            }
        }
    }

    .footer {
        padding: 12px 16px;
        border-top: 1px solid #eceeef;
        display: flex;
        justify-content: flex-end;
    }
}
</style>
